import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

const navItems = [
  { label: 'Start', path: '/' },
  { label: 'Über mich', path: '/ueber-mich' },
  { label: 'Praxis Borken', path: '/therapieangebote' },
  { label: 'Praxis Münster', path: '/praxis-muenster' },
  { label: 'Kursangebote', path: '/kursangebote' },
  { label: 'Kontakt', path: '/kontakt' },
];

const breadcrumbNames = {
  '/': 'Psychotherapie Münster',
  '/ueber-mich': 'Über mich',
  '/therapieangebote': 'Psychotherapie Borken',
  '/praxis-muenster': 'Psychotherapie Münster',
  '/praxiseinblick': 'Einblick',
  '/kursangebote': 'Kursangebote',
  '/kontakt': 'Kontakt',
  '/impressum': 'Impressum',
  '/datenschutz': 'Datenschutz',
};

export const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const currentBreadcrumb = breadcrumbNames[location.pathname] || 'Psychotherapie Münster';

  return (
    <header data-testid="main-header">
      {/* Top Navigation Bar - White */}
      <div className="bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <div className="flex justify-center items-center py-4">
            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8" data-testid="desktop-nav">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`text-xs uppercase tracking-widest transition-colors duration-200 ${
                    location.pathname === item.path 
                      ? 'text-[#555555]' 
                      : 'text-[#888888] hover:text-[#555555]'
                  }`}
                  style={{ letterSpacing: '0.15em' }}
                  data-testid={`nav-link-${item.path.replace('/', '') || 'home'}`}
                >
                  {item.label}
                </Link>
              ))}
            </nav>

            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setIsOpen(!isOpen)}
              data-testid="mobile-menu-button"
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isOpen && (
        <div className="md:hidden bg-white border-b border-gray-200" data-testid="mobile-nav">
          <nav className="flex flex-col px-4 py-4 space-y-2">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`py-2 text-sm uppercase tracking-wider ${
                  location.pathname === item.path 
                    ? 'text-[#555555]' 
                    : 'text-[#888888]'
                }`}
                data-testid={`mobile-nav-link-${item.path.replace('/', '') || 'home'}`}
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>
      )}

      {/* Sunset Image */}
      <div className="w-full">
        <img 
          src="https://psychotherapie-praxis-langer.de/Bilder/sunset.jpg"
          alt="Sonnenuntergang"
          className="w-full h-auto object-cover"
          style={{ maxHeight: '350px' }}
          loading="lazy"
        />
      </div>

      {/* Gray Breadcrumb Bar - FULL WIDTH like original */}
      <div 
        className="w-full py-2 sm:py-3 px-4"
        style={{ backgroundColor: '#808080' }}
      >
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center flex-wrap">
            {/* KATHARINA LANGER */}
            <Link 
              to="/" 
              className="text-white text-[10px] sm:text-xs uppercase tracking-widest hover:opacity-80 transition-opacity duration-200" 
              style={{ letterSpacing: '0.15em' }}
              data-testid="header-logo"
            >
              Katharina Langer
            </Link>
            
            {/* Arrow and Breadcrumb - Stylish arrow like original */}
            <span className="mx-2 sm:mx-3 text-white text-base font-light" style={{ fontFamily: 'Georgia, serif' }}>›</span>
            <span className="text-white text-[10px] sm:text-xs">
              {currentBreadcrumb}
            </span>
          </div>
        </div>
      </div>
    </header>
  );
};
